import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { Badge, Row } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { Button, CellFormatOptions, Icon } from '@octano/global-ui';
import Tooltip from '../../../../components/tooltip/Tooltip';
import { ClassroomApiResponse } from '../types';

type UseColumnsProps = {
  onEdit: (classroom: ClassroomApiResponse) => void;
  onDelete: (classroom: ClassroomApiResponse) => void;
  onToggleStatus: (classroom: ClassroomApiResponse) => void;
};

export const useColumns = ({
  onEdit,
  onDelete,
  onToggleStatus,
}: UseColumnsProps) => {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_CLASSROOMS);
  const history = useHistory();

  return [
    {
      columnName: 'name',
      headerText: t('table.headers.nameAndCode'),
      width: '15%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<ClassroomApiResponse>) => {
        return (
          <div>
            <div>{row.name}</div>
            <div className="text-muted">{row.code}</div>
          </div>
        );
      },
    },
    {
      columnName: 'description',
      headerText: t('table.headers.description'),
      width: '20%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<ClassroomApiResponse>) => {
        if (!row?.description) {
          return null;
        }
        // La descripción es libre y puede ser larga: se recorta a dos líneas y
        // el texto completo queda en el tooltip.
        return (
          <Tooltip
            id={`classroom-description-${row.id}`}
            text={row.description}
            placement="top"
          >
            <span
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {row.description}
            </span>
          </Tooltip>
        );
      },
    },
    {
      columnName: 'type',
      headerText: t('table.headers.type'),
      width: '12%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'campus',
      headerText: t('table.headers.campus'),
      width: '12%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'capacity',
      headerText: t('table.headers.capacity'),
      width: '8%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'state',
      headerText: t('table.headers.state'),
      width: '10%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<ClassroomApiResponse>) => {
        return (
          <Badge
            className="fs-14 fw-400 py-2 px-3 text-dark w-auto"
            style={{
              backgroundColor: row?.isEnabled ? '#CEF0DA' : '#FFE6A1',
            }}
            pill
          >
            {row?.isEnabled
              ? t('table.states.enabled')
              : t('table.states.disabled')}
          </Badge>
        );
      },
    },
    {
      columnName: 'manage',
      headerText: t('table.headers.manage'),
      width: '18%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<ClassroomApiResponse>) => {
        return (
          <div className="d-flex flex-row justify-content-center">
            {/*
              El botón muestra SIEMPRE la acción inversa al estado actual. Antes
              sólo se renderizaba para las salas habilitadas, así que una sala
              deshabilitada no se podía volver a habilitar desde el listado.
            */}
            <Button
              text={
                row.isEnabled
                  ? t('table.buttons.disable')
                  : t('table.buttons.enable')
              }
              size="sm"
              color="primary"
              onClick={() => onToggleStatus(row)}
              className="mr-2"
              outlined
            />
            <Button
              text={t('table.buttons.calendar')}
              size="sm"
              color="primary"
              onClick={() => history.push(`/classrooms/${row.id}/calendar`)}
            />
          </div>
        );
      },
    },
    {
      columnName: 'action',
      headerText: t('table.headers.action'),
      width: '5%',
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }: CellFormatOptions<ClassroomApiResponse>) => {
        return (
          <Row className="d-flex justify-content-center">
            <button className="btn" onClick={() => onEdit(row)}>
              <Icon name="edit" color={'primary'} />
            </button>
            <button className="btn" onClick={() => onDelete(row)}>
              <Icon name="trash" color={'primary'} />
            </button>
          </Row>
        );
      },
    },
  ];
};
